import { AnyError } from '@microsoft/portal-app/lib/IAnyError';
import { useMutation, UseMutationResult, useQueryClient } from 'react-query';
import { getRegionalAndLanguageUrl } from '../../redux/modules/graphApis';
import { ILanguageEntry, ILanguageUpdateEntry, LanguageActions } from '../models';
import { APITelemetry } from '../Telemetry';
import { QueryKey } from '../models/QueryKey';
import { useAuth } from '@iamexperiences/feature-auth';
import { useReportAPITelemetry } from './useReportAPITelemetry';
import { HttpRequestError } from '../../shared/HttpRequestError';
import { IRegionalAndLanguageSettingsQueryResult } from './useGetRegionalAndLanguageSettingQuery';
import { getMSGraphScopeAndEndPoint } from '../../shared/utils';
import { MSGraphScopes } from '../../shared/constants';

export const useSetLanguagePreferenceQuery = (
  actionName: LanguageActions,
  authoringLanguages: string[],
): UseMutationResult<unknown, AnyError, ILanguageEntry[], unknown> => {
  const { auth } = useAuth();
  const apiTelemetry: APITelemetry = new APITelemetry();
  const queryClient = useQueryClient();
  const { startTelemetryReport, stopTelemetryReport } = useReportAPITelemetry('setAuthoringLanguages');

  let newLanguages = [...authoringLanguages];
  let languagesPayload: ILanguageEntry[] = [];

  return useMutation(async (languages: ILanguageEntry[]) => {
    // The language won't be added if it has been added before
    if (
      actionName === LanguageActions.AddLastAuthoringLanguage &&
      authoringLanguages.find((lng) => lng === languages[0].bcp47Tag)
    ) {
      return;
    }

    languagesPayload = [...languages];
    newLanguages = [...authoringLanguages, languages[0].bcp47Tag];

    const updateEntry =
      actionName === LanguageActions.AddLastAuthoringLanguage
        ? buildUpdateEntryFromTags(newLanguages)
        : buildUpdateEntry([...languages]);

    apiTelemetry.startActivity('addLastAuthoringLanguage', { payload: newLanguages });
    startTelemetryReport({ payload: newLanguages });

    const url = getRegionalAndLanguageUrl();
    const token = await auth.acquireToken({ scopes: [getMSGraphScopeAndEndPoint(MSGraphScopes.UserReadWrite).scope] });

    const response = await fetch(url, {
      method: 'PATCH',
      headers: {
        Authorization: 'Bearer ' + token,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ authoringLanguages: updateEntry }),
    });

    if (!response.ok) {
      const error = new HttpRequestError(response.status, response.statusText);
      stopTelemetryReport('error', error);
      throw error;
    }

    queryClient.setQueryData<IRegionalAndLanguageSettingsQueryResult | undefined>(
      QueryKey.REGIONAL_AND_LANGUAGE_SETTINGS,
      (old) => {
        if (old) {
          return {
            ...old,
            authoringLanguages:
              actionName === LanguageActions.AddLastAuthoringLanguage
                ? newLanguages
                : languages.map((lng) => lng.bcp47Tag),
          };
        }
        return old;
      },
    );

    const logResult = {
      result: 0,
      payload: actionName === LanguageActions.AddLastAuthoringLanguage ? newLanguages : languagesPayload,
    };

    stopTelemetryReport('success', logResult);
    return response;
  });
};

const buildUpdateEntryFromTags = (languages: string[]): ILanguageUpdateEntry[] => {
  return languages.map((item) => ({ locale: item }));
};

const buildUpdateEntry = (languages: ILanguageEntry[]): ILanguageUpdateEntry[] => {
  return languages.map((item) => ({ locale: item.bcp47Tag }));
};
